import { useState, useEffect } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { shallow } from 'zustand/shallow';
import { useBulkCollectionsFilterStore } from '../../../../../store/bulk-collections-filter-store';
import { useBulkCollectionsQueryBase } from './use-bulk-collections-query-base';

export type UseBulkCollectionsInfiniteScrollReturn = {
  data: GetPatientsBalanceResponse_PatientBalance[]; // Accumulated data across all pages
  isLoading: boolean; // True on initial load
  isFetchingNextPage: boolean; // True when loading more pages
  hasNextPage: boolean;
  loadMore: () => void;
};

export const useBulkCollectionsInfiniteScroll = ({
  enabled,
}: {
  enabled: boolean;
}): UseBulkCollectionsInfiniteScrollReturn => {
  const [accumulatedData, setAccumulatedData] = useState<GetPatientsBalanceResponse_PatientBalance[]>([]);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Reset accumulated data when filters change
  useEffect(() => {
    const unsubscribe = useBulkCollectionsFilterStore.subscribe(
      (state) => ({ balanceType: state.balanceType, searchValue: state.searchValue }),
      () => {
        setAccumulatedData([]);
        setPageToken(undefined);
        setIsInitialLoad(true);
      },
      { equalityFn: shallow }
    );
    return unsubscribe;
  }, []);

  const {
    data: mappedData,
    isFetching,
    nextPageToken,
  } = useBulkCollectionsQueryBase({
    enabled,
    pageToken,
    keepPreviousData: true,
  });

  // Accumulate data when new page arrives
  useEffect(() => {
    if (!enabled || isFetching) return;

    if (isInitialLoad) {
      // First page: set accumulated data (may be empty if API returned [])
      setAccumulatedData(mappedData);
      setIsInitialLoad(false);
    } else if (pageToken && mappedData.length > 0) {
      // Subsequent pages: append to accumulated data, deduping by patientId to guard against refetch duplicates
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((p) => p.patientId));
        return [...prev, ...mappedData.filter((p) => !existingIds.has(p.patientId))];
      });
    }
  }, [enabled, isFetching, mappedData, pageToken, isInitialLoad]);

  const loadMore = () => {
    if (!isFetching && nextPageToken) {
      setPageToken(nextPageToken);
    }
  };

  return {
    data: accumulatedData,
    isLoading: enabled && isInitialLoad,
    isFetchingNextPage: !isInitialLoad && isFetching,
    hasNextPage: !!nextPageToken,
    loadMore,
  };
};
